import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types';

export const userApi = {
  async fetchUsers() {
    console.log('Fetching users from database...');
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      throw error;
    }

    console.log('Raw user data from database:', data);

    if (data) {
      const formattedUsers: User[] = data.map(user => ({
        id: user.id,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email,
        service: user.service || '',
        role: user.role as 'user' | 'admin',
        isActive: user.is_active || false,
        createdAt: user.created_at || ''
      }));
      
      console.log('Formatted users:', formattedUsers);
      return formattedUsers;
    }

    return [];
  },

  async deleteUser(userId: string) {
    console.log('Deleting user:', userId);
    
    const { error } = await supabase
      .from('user_profiles')
      .delete()
      .eq('id', userId);

    if (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  async toggleUserStatus(userId: string, newStatus: boolean) {
    console.log(`Toggling user ${userId} status to ${newStatus}`);

    const { error } = await supabase
      .from('user_profiles')
      .update({ is_active: newStatus })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user status:', error);
      throw error;
    }

    console.log('Status update successful');
  },

  async toggleUserRole(userId: string, newRole: 'user' | 'admin') {
    console.log(`Toggling user ${userId} role to ${newRole}`);
    
    const { error } = await supabase
      .from('user_profiles')
      .update({ role: newRole })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user role:', error);
      throw error;
    }

    console.log('Role update successful');
  },

  async checkUserExists(email: string) {
    const { data: existingUser, error: checkError } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('email', email);

    if (checkError) {
      console.error('Error checking existing user:', checkError);
      throw checkError;
    }

    return existingUser && existingUser.length > 0;
  },

  async addUser(userData: {
    email: string;
    firstName: string;
    lastName: string;
    service: string;
    role: 'user' | 'admin';
    isActive: boolean;
  }) {
    console.log('Adding new user:', userData);
    
    const { data, error } = await supabase.rpc('insert_new_user', {
      p_email: userData.email,
      p_first_name: userData.firstName,
      p_last_name: userData.lastName,
      p_service: userData.service,
      p_role: userData.role,
      p_is_active: userData.isActive
    });

    if (error) {
      console.error("Erreur d'insertion:", error);
      if (error.code === '23503') {
        throw new Error("No authenticated user found");
      }
      throw error;
    }

    return data;
  },

  async fetchEmails() {
    // Récupère tous les utilisateurs actifs (authentifiés)
    const { data, error } = await supabase
      .from('user_profiles')
      .select('email, first_name, last_name, is_active')
      .eq('is_active', true);

    if (error) {
      console.error('Erreur lors de la récupération des emails utilisateurs :', error);
      return [];
    }
    // Retourne une liste type {label, value}
    return (data || []).map(user => ({
      label: user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name} <${user.email}>`
        : user.email,
      value: user.email
    }));
  }
};
